import { ErrorCodes, ErrorStatusMap } from '../constants/errors'
import type { ErrorCode } from '../constants/errors'

export class AppError extends Error {
  public readonly code: ErrorCode
  public readonly statusCode: number
  public readonly details?: unknown
  public readonly isOperational: boolean

  constructor(
    message: string,
    options: {
      code?: ErrorCode
      statusCode?: number
      details?: unknown
      isOperational?: boolean
    } = {},
  ) {
    super(message)
    this.name = 'AppError'
    const code = options.code || ErrorCodes.INTERNAL_ERROR
    this.code = code
    this.statusCode = options.statusCode || ErrorStatusMap[code] || 500
    this.details = options.details
    this.isOperational = options.isOperational ?? true
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, this.constructor)
    }
  }

  toJSON(): Record<string, unknown> {
    return {
      error: this.message,
      code: this.code,
      statusCode: this.statusCode,
      details: this.details,
      isOperational: this.isOperational,
    }
  }

  static badRequest(message = 'Bad request', details?: unknown): AppError {
    return new AppError(message, { code: ErrorCodes.BAD_REQUEST, details })
  }

  static unauthorized(message = 'Authentication required'): AppError {
    return new AppError(message, { code: ErrorCodes.UNAUTHORIZED })
  }

  static forbidden(message = 'Insufficient permissions'): AppError {
    return new AppError(message, { code: ErrorCodes.FORBIDDEN })
  }

  static notFound(message = 'Resource not found'): AppError {
    return new AppError(message, { code: ErrorCodes.NOT_FOUND })
  }

  static conflict(message: string, details?: unknown): AppError {
    return new AppError(message, { code: ErrorCodes.CONFLICT, details })
  }

  static rateLimited(message = 'Too many requests', retryAfter?: number): AppError {
    return new AppError(message, {
      code: ErrorCodes.RATE_LIMITED,
      details: retryAfter ? { retryAfter } : undefined,
    })
  }

  static internal(message = 'Internal server error'): AppError {
    return new AppError(message, { code: ErrorCodes.INTERNAL_ERROR, isOperational: false })
  }

  static notImplemented(message = 'Not implemented'): AppError {
    return new AppError(message, { code: ErrorCodes.NOT_IMPLEMENTED })
  }

  static validation(message: string, details?: unknown): AppError {
    return new AppError(message, { code: ErrorCodes.VALIDATION_ERROR, details })
  }

  static externalProvider(message: string, details?: unknown): AppError {
    return new AppError(message, { code: ErrorCodes.EXTERNAL_PROVIDER_ERROR, details })
  }
}
